import type { AxiosInstance } from "axios";
import type { AssessmentProficiencyConfig } from "~/types/assessmentProficiency";
import HttpFactory from "~/repository/factory";
import type {
  AssessmentAuthoringDocument,
  AssessmentAuthoringUpdatePayload,
} from "~/types/assessmentAuthoring";
import type { PaginatedResponse } from "~/types/admin/assessment";
import type {
  SchoolAssessment,
  SchoolAssessmentPublication,
  SchoolAssessmentContact,
  SchoolPublicResponse,
  SchoolAssessmentContactRequest,
} from "~/types/schoolAssessments";

const assessments = "/api/assessment/school-assessments/";
const contacts = "/api/assessment/school-assessment-contacts/";
const attempts = "/api/assessment/school-assessment-attempts/";

export default class SchoolAssessmentsModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "school");
  }

  list(page = 1) {
    return this.get<PaginatedResponse<SchoolAssessment>>(assessments, { page });
  }

  getAssessment(id: number) {
    return this.get<SchoolAssessment>(`${assessments}${id}/`);
  }

  setTeacherSharing(id: number, shareWithTeachers: boolean) {
    return this.patch<SchoolAssessment>(`${assessments}${id}/`, {
      share_with_teachers: shareWithTeachers,
    });
  }

  setPublicContactRequestEnabled(id: number, enabled: boolean) {
    return this.patch<SchoolAssessment>(`${assessments}${id}/`, {
      public_contact_request_enabled: enabled,
    });
  }

  setPublicBookingMeetingType(id: number, meetingTypeId: number | null) {
    return this.patch<SchoolAssessment>(`${assessments}${id}/`, {
      public_booking_meeting_type_id: meetingTypeId,
    });
  }

  createAssessment(input: {
    title: string;
    assessment_type: "quiz" | "exam" | "proficiency";
    proficiency?: AssessmentProficiencyConfig | null;
    client_request_id: string;
  }) {
    return this.post<SchoolAssessment>(assessments, input);
  }

  getAssessmentAuthoring(id: number) {
    return this.get<AssessmentAuthoringDocument>(
      `${assessments}${id}/authoring/`,
    );
  }

  updateAssessmentAuthoring(
    id: number,
    input: AssessmentAuthoringUpdatePayload | FormData,
  ) {
    return this.put<AssessmentAuthoringDocument>(
      `${assessments}${id}/authoring/`,
      input,
    );
  }

  publish(
    id: number,
    input: { share_with_teachers: boolean; show_public_result: boolean },
  ) {
    return this.post<SchoolAssessmentPublication>(
      `${assessments}${id}/publish/`,
      input,
    );
  }

  unpublish(id: number) {
    return this.post<SchoolAssessmentPublication>(
      `${assessments}${id}/unpublish/`,
      {},
    );
  }

  listContacts(id: number, page = 1, hasPendingContactRequest = false) {
    return this.get<PaginatedResponse<SchoolAssessmentContact>>(
      `${assessments}${id}/contacts/`,
      {
        page,
        has_pending_contact_request: hasPendingContactRequest || undefined,
      },
    );
  }

  getContact(id: number) {
    return this.get<SchoolAssessmentContact>(`${contacts}${id}/`);
  }

  markContactRequestContacted(id: number) {
    return this.patch<SchoolAssessmentContactRequest>(
      `/api/assessment/school-assessment-contact-requests/${id}/`,
      { status: "contacted" },
    );
  }

  convertContact(
    id: number,
    input: { create_blocked: boolean; template_id: string | null },
  ) {
    return this.post<{ student_id: number; already_linked: boolean }>(
      `${contacts}${id}/convert/`,
      input,
    );
  }

  gradeResponse(
    attemptId: number,
    responseId: number,
    input: { score_earned: number; is_correct: boolean; feedback: string },
  ) {
    return this.post<SchoolPublicResponse>(
      `${attempts}${attemptId}/responses/${responseId}/grade/`,
      input,
    );
  }
}
